import { useEffect, useState } from 'react';
import C from 'classnames';
import { useTranslation } from 'react-i18next';
import LicensedCapacity from 'components/molecules/licensed-capacity';
import LinkChainIcon from 'components/atoms/icons/link-cain';
import ChevronTopIcon from 'components/atoms/icons/chevron-top';
import TelephoneXIcon from 'components/atoms/icons/telephone-x';
import ArrowLeftRightIcon from 'components/atoms/icons/arrow-left-right';
import CloseIcon from 'assets/img/close.svg?react';
import { PauseGlyph } from 'components/atoms/icons/pause';
import MicIcon from 'components/atoms/icons/mic';
import MicMuteIcon from 'components/atoms/icons/mic-mute';
import KeypadIcon from 'components/atoms/icons/keypad';

import ControlTile from './control-tile';
import StateGlyph from './state-glyph';
import { formatAgentStateName } from './format-state-name';
import Keypad from './keypad';
import Transfer from './transfer';
import CallSummary from './call-summary';
import CallFailed from './call-failed';
import { DragGripGlyph, MinusGlyph } from './header-glyphs';
import s from './styles.module.scss';

import type { FC } from 'react';
import type { ICallState, TTileTone } from './types';

interface IProps {
  /** The modal's open flag. The panel outlives a close (destroyOnClose={false}),
      so it needs to know when one happened to put its drawers away. */
  isOpen: boolean;
  callState: ICallState;
  patientName?: string;
  phoneNumber: string;
  isRpmCall: boolean;
  patientGuid?: string;
  interactiveTotal: string;
  isCollapsed: boolean;
  onHold: () => void;
  onMute: () => void;
  onDigit: (digit: string) => void;
  onTransferDial: (target: { endpointId?: string; rawNumber?: string }) => void;
  onTransferSwap: () => void;
  onTransferJoin: () => void;
  onTransferComplete: () => void;
  onTransferCancel: () => void;
  onEndCall: () => void;
  onLinkReadings: () => void;
  onMinimize: () => void;
  onClose: () => void;
  onToggleCollapse: () => void;
}

const formatClock = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
    2,
    '0',
  )}`;
};

const CallPanel: FC<IProps> = ({
  isOpen,
  callState,
  patientName,
  phoneNumber,
  isRpmCall,
  patientGuid,
  interactiveTotal,
  isCollapsed,
  onHold,
  onMute,
  onDigit,
  onTransferDial,
  onTransferSwap,
  onTransferJoin,
  onTransferComplete,
  onTransferCancel,
  onEndCall,
  onLinkReadings,
  onMinimize,
  onClose,
  onToggleCollapse,
}) => {
  const { t } = useTranslation();
  const [drawer, setDrawer] = useState<'none' | 'keypad' | 'transfer'>('none');

  // The panel is never unmounted, so a drawer left open would survive into the
  // next call — reopening at 650px with the previous call's dialled digits
  // still on the keypad. Closing the drawer also unmounts Keypad and Transfer,
  // which is what clears their entered digits and search text.
  useEffect(() => {
    if (!isOpen) setDrawer('none');
  }, [isOpen]);

  const {
    phase,
    elapsedSeconds,
    heldSeconds,
    isHeld,
    isMuted,
    agentStateName,
    isAgentRoutable,
    transfer,
    failed,
    failureStateName,
  } = callState;

  const isIdle = phase === 'idle';
  const isConnecting = phase === 'connecting';
  const isConnected = phase === 'connected';
  const isEnded = phase === 'ended';
  const hasTransfer = !!transfer;

  // A third party can land on the contact while the keypad is open (a
  // supervisor-added leg, another Connect window), and the transfer drawer takes
  // the space over. Drop the keypad selection with it: the transfer drawer pins
  // itself open on `transfer` alone, so nothing moves on screen now, but it
  // means cancelling the transfer returns the agent to the tile row instead of
  // popping the old keypad back open over it.
  useEffect(() => {
    if (hasTransfer) setDrawer('none');
  }, [hasTransfer]);

  // Neither drawer has anything to act on once the contact is gone, so they fold
  // themselves away rather than leaving the agent to close them. A transfer in
  // progress pins its own drawer open — the controls for a three-way call are
  // the only way out of it.
  const isTransferOpen = (drawer === 'transfer' || !!transfer) && !isEnded;
  // The keypad yields to a live transfer: a third party can land on the
  // contact while the keypad is open (a supervisor-added leg, another Connect
  // window), and the transfer pins its own drawer — two drawers stacked would
  // overflow the height-pinned panel and clip the terminal controls.
  const isKeypadOpen = drawer === 'keypad' && !isEnded && !isTransferOpen;
  const isDrawerOpen = isKeypadOpen || isTransferOpen;

  // Hold and Transfer both need a CONNECTED contact. Mute is available while
  // CONNECTING as an ARMED mute: Connect's server-side mute needs an answered
  // call, so a ring-time click keeps the tile engaged and the container applies
  // it the instant the patient picks up (see applyMuted in ../index.tsx).
  // The KEYPAD is intentionally available while CONNECTING — carriers and IVR
  // spam gates ask the caller to dial a digit before the call completes, so
  // gating DTMF on isOnCall makes those calls impossible. While IDLE there is
  // no contact at all, so every tile is unavailable.
  const holdTone: TTileTone = (() => {
    if (isEnded || isConnecting || isIdle) return 'unavailable';
    return isHeld ? 'engaged' : 'default';
  })();
  const muteTone: TTileTone = (() => {
    if (isEnded || isIdle) return 'unavailable';
    return isMuted ? 'engaged' : 'default';
  })();
  const keypadTone: TTileTone = isEnded || isIdle ? 'unavailable' : 'default';
  // Transfer needs a connected contact to add a party to. Engaged whenever a
  // third party is on the line, so the tile row reports the transfer even after
  // the drawer is closed.
  const transferTone: TTileTone = (() => {
    if (transfer) return 'engaged';
    return isConnected ? 'default' : 'unavailable';
  })();

  const callType = isRpmCall ? 'RPM' : 'CCM';
  // patientName is optional in connectDetails — the modal already falls back to
  // the product name, so the panel must too rather than showing an empty line.
  const title = patientName || t('connectPanel.fallbackTitle');

  const isFailed = isEnded && failed;

  const stateKey = (() => {
    if (isFailed) return 'failed';
    return isHeld && isConnected ? 'held' : phase;
  })();
  const stateLabel = (() => {
    if (isIdle) return t('connectPanel.state.idle');
    if (isConnecting) return t('connectPanel.state.connecting');
    if (isFailed) return t('connectPanel.state.failed');
    if (isEnded) return t('connectPanel.state.ended');
    if (isHeld) {
      return t('connectPanel.state.held', { held: formatClock(heldSeconds) });
    }
    return t('connectPanel.state.connected');
  })();

  return (
    <div
      className={C(s.panel, {
        [s['panel-drawer']]: isDrawerOpen,
        [s['panel-collapsed']]: isCollapsed,
      })}
      data-cy="call-panel"
    >
      {/* The whole bar is the drag handle. DraggableModal starts a drag only
          from an element carrying data-draggable, so the identity block opts in
          and the grip turns off pointer events to let clicks fall through. */}
      <div className={s.header} data-draggable>
        <DragGripGlyph className={s['header-grip']} />

        <div className={s['header-identity']} data-draggable>
          <span className={s['header-name']} data-draggable title={title}>
            {title}
          </span>
          <span className={s['header-meta']} data-draggable>
            {phoneNumber
              ? t('connectPanel.callTypeLine', {
                  type: callType,
                  phone: phoneNumber,
                })
              : t('connectPanel.callTypeOnly', { type: callType })}
          </span>
        </div>

        <button
          type="button"
          className={s['header-btn']}
          onClick={onMinimize}
          aria-label={t('connectPanel.minimize')}
          data-cy="call-panel-minimize"
        >
          <MinusGlyph className={s['header-minimize']} />
        </button>
        <button
          type="button"
          className={s['header-btn']}
          onClick={onClose}
          aria-label={t('connectPanel.close')}
          data-cy="call-panel-close"
        >
          <CloseIcon className={s['header-close']} width={11} height={11} />
        </button>
      </div>

      {!isCollapsed && (
        <>
          <div className={C(s['state-row'], s[`state-${stateKey}`])}>
            <StateGlyph
              phase={phase}
              isHeld={isHeld && isConnected}
              className={s['state-glyph']}
            />
            <span className={s['state-label']}>{stateLabel}</span>
            <span className={s.spacer} />
            <span className={s.timer} data-cy="call-timer">
              {formatClock(elapsedSeconds)}
            </span>
          </div>

          {/* Connected only, not the ring: an agent cannot know whether a
              transfer is needed until they have talked to the patient, and
              `GetCurrentUserData` shares a 2 req/s account-wide quota that
              pre-dial polling would spend on calls that never transfer. */}
          {isConnected && (
            <div className={s['capacity-wrapper']}>
              <LicensedCapacity
                patientGuid={patientGuid}
                isOnCall={isConnected}
                variant="compact"
                className={s.capacity}
              />
            </div>
          )}

          {/* A wrap-up board with 00:00 of talk time explains nothing when the
              ring died on its own, so a failed contact gets the notice instead. */}
          {isEnded &&
            (isFailed ? (
              <CallFailed failureStateName={failureStateName} />
            ) : (
              <CallSummary
                talkTime={formatClock(elapsedSeconds)}
                interactiveTotal={interactiveTotal}
                callType={callType}
              />
            ))}

          {isKeypadOpen && (
            <Keypad onDigit={onDigit} onHide={() => setDrawer('none')} />
          )}

          {isTransferOpen && (
            <Transfer
              transfer={transfer}
              patientName={title}
              onDial={onTransferDial}
              onSwap={onTransferSwap}
              onJoin={onTransferJoin}
              onComplete={onTransferComplete}
              onCancel={onTransferCancel}
              onHide={() => setDrawer('none')}
            />
          )}

          {!isEnded && !isDrawerOpen && (
            <div className={s.tiles}>
              <ControlTile
                icon={<PauseGlyph width={18} height={18} />}
                label={
                  isHeld
                    ? t('connectPanel.tile.held')
                    : t('connectPanel.tile.hold')
                }
                tone={holdTone}
                onClick={onHold}
                dataCy="tile-hold"
              />
              <ControlTile
                icon={
                  isMuted ? (
                    <MicMuteIcon width={17} height={17} />
                  ) : (
                    <MicIcon width={17} height={17} />
                  )
                }
                label={
                  isMuted
                    ? t('connectPanel.tile.muted')
                    : t('connectPanel.tile.mute')
                }
                tone={muteTone}
                onClick={onMute}
                dataCy="tile-mute"
              />
              <ControlTile
                icon={<KeypadIcon width={17} height={17} />}
                label={t('connectPanel.tile.keypad')}
                tone={keypadTone}
                onClick={() => setDrawer('keypad')}
                dataCy="tile-keypad"
              />
              <ControlTile
                icon={
                  <ArrowLeftRightIcon
                    color="currentColor"
                    width={17}
                    height={17}
                  />
                }
                label={
                  transfer
                    ? t('connectPanel.tile.transferring')
                    : t('connectPanel.tile.transfer')
                }
                tone={transferTone}
                onClick={() => setDrawer('transfer')}
                dataCy="tile-transfer"
              />
            </div>
          )}

          {/* Hidden once the contact ends: the callEnded event that carries the
              linked readings has already fired, so on the wrap-up board this
              would be an offer nothing can honour. The summary flexes into the
              vacated space, so the panel holds its height. */}
          {!isEnded && (
            <div
              className={C(s['action-wrapper'], {
                [s['action-wrapper-after-keypad']]: isDrawerOpen,
              })}
            >
              <button
                type="button"
                className={s['secondary-action']}
                onClick={onLinkReadings}
                disabled={!isConnected || !isRpmCall}
                data-cy="link-readings"
              >
                <LinkChainIcon width={15} height={15} />
                {t('connectPanel.linkReadings')}
              </button>
            </div>
          )}

          <div className={s['terminal-wrapper']}>
            {isEnded ? (
              <button
                type="button"
                className={s.finish}
                onClick={onClose}
                data-cy="finish-and-close"
              >
                {t('connectPanel.finishAndClose')}
              </button>
            ) : (
              <button
                type="button"
                className={C(s['end-call'], {
                  [s['end-call-cancel']]: isConnecting || isIdle,
                })}
                // While IDLE nothing has been dialled, so there is no contact
                // to hang up — cancelling means closing the window.
                onClick={isIdle ? onClose : onEndCall}
                data-cy="end-call"
              >
                {!isConnecting && !isIdle && (
                  <TelephoneXIcon color="currentColor" />
                )}
                {isConnecting || isIdle
                  ? t('connectPanel.cancelCall')
                  : t('connectPanel.endCall')}
              </button>
            )}
          </div>
        </>
      )}

      <div className={s.footer}>
        {/* DISPLAY ONLY. connectService never calls agent.setState() — today's
            "Offline" dropdown is the Amazon CCP's, inside the iframe. No caret,
            no hover plate, not focusable. */}
        {!!agentStateName && (
          <span className={s['agent-state']} data-cy="agent-state">
            <span
              className={C(s['agent-dot'], {
                [s['agent-dot-routable']]: isAgentRoutable,
              })}
            />
            {formatAgentStateName(agentStateName)}
          </span>
        )}
        <span className={s.spacer} />
        <button
          type="button"
          className={s['footer-btn']}
          onClick={onToggleCollapse}
          aria-label={
            isCollapsed ? t('connectPanel.expand') : t('connectPanel.collapse')
          }
          data-cy="call-panel-collapse"
        >
          <ChevronTopIcon
            width={14}
            height={14}
            className={C(s['footer-chevron'], {
              [s['footer-chevron-down']]: isCollapsed,
            })}
          />
        </button>
      </div>
    </div>
  );
};

export default CallPanel;
